import {
  Component, EventEmitter, Input, OnDestroy, OnInit,
  Output
} from '@angular/core';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  Observable, Subject, switchMap, takeUntil
} from 'rxjs';

import {
  IMemberProviderNetworkEntry,
  MemberProviderNetworkService
} from '@sondermind/data-access/provider-facing/provider-profile';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { USStateAbbrevMap } from '@sondermind/utilities/states-list';

interface IStateForm {
  state: FormControl<string>;
}

@Component({
  selector: 'member-provider-network-list',
  templateUrl: './provider-network-list.component.html',
  styleUrls: ['./provider-network-list.component.scss'],
  standalone: false
})
export class ProviderNetworkListComponent implements OnInit, OnDestroy {
  @Input() user!: ProviderUser;
  @Output() isLoaded = new EventEmitter<void>();

  private destroyed$ = new Subject<void>();

  loaded: boolean = false;
  loadingNewState: boolean = false;
  networks: IMemberProviderNetworkEntry[] = [];
  stateForm: FormGroup<IStateForm>;
  stateOptions: Array<DropdownOption<string>> = [];
  initialState: string = null;

  constructor(
    private memberProviderNetworkService: MemberProviderNetworkService
  ) { }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  loadInitialData(): void {
    this.memberProviderNetworkService.getStates(this.user.personaProviderId).pipe(
      switchMap((states) => {
        this.populateStateOptions(states ?? []);
        this.initialState = this.getInitialState();
        this.initializeForm();
        this.subscribeToForm();
        return this.loadNetworks(this.initialState);
      })
    ).subscribe((networks) => {
      this.networks = networks;
      this.loaded = true;
      this.isLoaded.emit();
    });
  }

  populateStateOptions(states: string[]): void {
    const stateOptions: Array<DropdownOption<string>> = [];
    states.sort().forEach((option) => {
      const dropdownOption = <DropdownOption<string>>{
        name: USStateAbbrevMap[option] ?? option,
        value: option
      };
      stateOptions.push(dropdownOption);
    });
    this.stateOptions = stateOptions;
  }

  getInitialState(): string {
    const states = this.stateOptions.map((x) => x.value);

    if (states.includes(this.user.practiceState)) {
      return this.user.practiceState;
    }
    return states?.length ? states.at(0) : '';
  }

  initializeForm(): void {
    this.stateForm = new FormGroup<IStateForm>({
      state: new FormControl(this.initialState, [Validators.required])
    });
  }

  subscribeToForm(): void {
    this.stateForm.valueChanges.pipe(
      takeUntil(this.destroyed$),
      switchMap((results) => {
        this.loadingNewState = true;
        return this.loadNetworks(results.state);
      })
    ).subscribe({
      next: (data) => {
        this.networks = data;
        this.loadingNewState = false;
      },
      error: () => {
        this.loadingNewState = false;
      }
    });
  }

  loadNetworks(state: string): Observable<IMemberProviderNetworkEntry[]> {
    return this.memberProviderNetworkService.list({
      page: { size: 500 },
      filter: [
        { key: 'state', value: state }
      ]
    });
  }
}
